import { i18n } from "@/plugins/i18n";
import { StoreModule } from "@/constants/store.ts";
import {
  addGenericMetadataToRequest,
  addWorkspaceHeaderIfNeeded,
  buildTicketActionRequest,
  convertTicketFiltersForBackend,
  getTicketBulkActionRequest,
  getTicketDetailsRequest,
  getTicketsRequest,
  handleBulkActionSnackbar,
  handleTicketActionSnackbar,
  handleTicketMetadata,
} from "./adapters";
import {
  AnomalyIpThreatLevel,
  type AsrDetectionReason,
  EDRTicketDescriptionCode,
  SocStatus,
  TicketAction,
  TicketActionSeverity,
  TicketEventSubtype,
  TicketTrigger,
  TicketType,
  ViolationDirection,
} from "@/constants/tickets";
import { axiosInstance } from "@/plugins/https";
import { defineStore } from "pinia";
import { useFiltersStore } from "@/_store/filters.module";
import type { OpenSearchCursor, Pagination } from "@/types";
import { useTicketStore } from "@/_store/tickets/ticket.module";
import api from "@/_helpers/api";
import { type ISelection, useSelectorStore } from "@/_store/selector.module";
import { Service } from "@/constants/cloud-apps";
import { handleVirtualScrollData } from "@coronet/cui";
import { downloadFile } from "@/_helpers/download";
import { getSelectionAsStringsArray } from "@/_helpers/selection";
import { parseOpenSearchQuery } from "@/_helpers/pagination";
import { applyQuickFixAction, type QuickFixPayload } from "@/_helpers/ticket-helpers";
import type { SubscriptionModule } from "@/constants/workplaces";
import { AxiosError, type AxiosResponse } from "axios";
import type { EdrProcessStatus } from "@/constants/edr";
import { type ProxyDirection, ProxySecurityMode } from "@/constants/email-proxy";
import type { ActivityLogItem } from "@/_store/activity-logs.module";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { firstPageQueryEntityActivityLogs } from "@/constants/general.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useGatewaySettingsStore } from "@/_store/gateway-settings.module.ts";
import { createAbortController } from "@coronet/cui";

export interface TicketActionWithSeverity {
  name: TicketAction;
  severity: TicketActionSeverity;
}

export interface AnomalyDetection {
  type: string;
  originalTime: number;
  ip: string;
  country: string;
  regionName: string;
  suspicious: boolean;
  threatTypes: string[];
  proxyType?: string;
  vpnService: string;
  host?: string;
  threatLevel?: AnomalyIpThreatLevel;
  threatDetectionDate: number;
  orgName: string;
}

export interface ImpossibleTravelerLogin {
  ip: string;
  isp: string;
  countryName: string;
  countryCode: string;
  timestamp: number;
  service: Service;
  stateName?: string;
  cityName: string;
}
export interface ImpossibleTravelerDetails {
  distance: number;
  previousLogin: ImpossibleTravelerLogin;
  currentLogin: ImpossibleTravelerLogin;
}

export interface AnomalyDetectionMetaData {
  records: AnomalyDetection[];
}

export interface DeviceMetaData {
  deviceId: string;
  login: string;
  userName: string;
  upn: string;
  enrollmentCode: string;
  hostname: string;
  model: string;
  osType: string;
  osVersion: string;
  osVersionDisplayName: string;
  removed: boolean;
  unencryptedFileStorage: FileStorage;
  usersWithoutPassword: DeviceUserMetaData[];
  scanId: string;
  scannedFileStorage: FileStorage;
  dlpEngineVersion: string;
}

export interface FileStorage {
  name: string;
  model: string;
  interfaceType: string;
}

export interface DeviceUserMetaData {
  login: string;
  userName: string;
  upn: string;
}

export interface EmailMetaData {
  senderName?: string;
  senderIp?: string;
  senderEmail: string;
  recipients: string[];
  detectionType: string;
  subject: string;
  attachment?: Attachment; // For legacy tickets support
  attachments: Attachment[];
  directions: string[];
  messageHash: number;
  proxyEmailMetadata: boolean;
  proxyRecipientsDomain?: string;
  emailProxyDirection?: ProxyDirection;
  internetMessageId?: string;
  content?: string;
  proxyBlockingMode?: ProxySecurityMode;
  proxyBlockedRecipients?: string[];
  senderCountry?: string;
  senderTld?: string;
}

export interface MaliciousUrlClickMetaData {
  clickedUrl: string;
  clickCount: number;
}

export interface SecurityAwarenessMetaData {
  affectedUsersList: string[];
  campaignName: string;
  emailTemplate: string;
  emailSender: string;
  attachmentType: string;
  securityTrainingContents: string[];
}

export interface MalwareMetaData {
  fileId: string;
  parentFolderId: string;
  fileName: string;
  path: string;
  virusName: string;
  threatType: string;
  threatStatus: string;
  mimeType: string;
  fileHash: string;
  containerHash?: string;
  fileSize?: number;
  owner: string;
  sharedWith: Array<string>;
}

export interface NetworkMetaData {
  ssid: string;
  bssids: string[]; // Using an array instead of a Set
  networkSecurity: string;
  indicators: string[]; // Using an array instead of a Set
}

export interface BlockedConnectionMetaData {
  pid: string;
  parentProcessName: string;
  parentPid: string;
  timestamp: number;
}

export interface AsrDetails {
  detectionReason: AsrDetectionReason;
  parentProcessHash: string;
  parentProcessName?: string;
}

export interface EdrMetaData {
  shortDescription: string;
  fullDescription: string;
  processHash: string;
  processName: string;
  affectedDevicesCount: number;
  showExpandedFindings: boolean;
  mitre: EdrMitreTechniqueData[];
  processStatus: EdrProcessStatus;
  allowBlockTimestamp?: number;
  descriptionCode: EDRTicketDescriptionCode;
  blockedConnectionMetaData?: BlockedConnectionMetaData;
  asrDetails?: AsrDetails;
}

export interface SystemMetaData {
  proxyMisconfiguration: ProxyMisconfigurationData;
}

export interface ProxyMisconfigurationData {
  domain: string;
  lastFailedTestTime: number;
}

export interface EdrMitreTechniqueData {
  title: string;
  description: string;
  techniqueId: string;
  link: string;
}

export interface InfectedProcessMetaData {
  type: InfectedProcessType;
  threatType: AtcThreatType;
  processGroups: Array<ProcessGroup>;
  suspiciousCharacteristics: string;
  suspectedMaliciousProcessName: string;
}

export enum InfectedProcessType {
  PROCESS = "process",
  GROUP = "group",
}

export enum AtcThreatType {
  INFECTED_PROCESS = "infectedProcess",
  ML_INFECTED_PROCESS = "mlInfectedProcess",
  BETA_INFECTED_PROCESS = "betaInfectedProcess",
  EXTERNAL_SOURCE_INFECTED_PROCESS = "externalSourceInfectedProcess",
  INFECTED_GROUP = "infectedGroup",
  ML_INFECTED_GROUP = "mlInfectedGroup",
}

export interface ProcessGroup {
  id: string;
  path: string;
  detected: boolean;
  sha256: string;
}

export interface Attachment {
  fileName: string;
  fileSize: number;
}

export enum FindingDetectedInType {
  EMAIL_BODY = "body",
  ATTACHMENT = "attachment",
  SUBJECT = "subject",
}

export interface FileMetaData {
  sharedBy?: string;
  sharedWith?: string[];
  sharedFolderPath?: string;
  fileName?: string;
  fileSize?: number;
  filePath?: string;
  path?: string;
  owner?: string;
  lastModifiedBy?: string;
  lastModifiedTime?: number;
}

export interface Finding {
  type: string;
  quote: string;
  longQuote: string;
  filePath: string;
  virusName?: string;
  typeUiView?: string;
  source?: FindingDetectedInType;
}

export interface Location {
  ip: string;
  countryName: string;
  countryCode: string;
  host?: string;
  regionName?: string;
}

export interface UserDataAccessViolation {
  violationDirection: ViolationDirection;
  users: string[];
}

export interface Ticket {
  eventId: string;
  eventType: TicketType;
  eventTrigger: TicketTrigger;
  processed: boolean;
  eventTime: number; // timestamp
  from: string;
  to: string[];
  userDataAccessViolation: UserDataAccessViolation;
  suspiciousVoteCount: number;
  safeVoteCount: number;
  moduleName: SubscriptionModule;
  potentiallyProtectableUsers: string[];
  workspaceId?: string;
  workspaceName?: string;
  processName?: string;
  usersAffected?: number;
  socStatus?: SocStatus;
  processStatus?: EdrProcessStatus;
  lastLoggedInUsers?: string[];
  hasUnreadComment?: boolean;
  remediationRequired?: boolean;
}

export interface TicketDetails {
  activityLogs: ActivityLogItem[];
  activityLogsTotal: number;
  workspaceId?: string;
  processed: boolean;
  eventId: string;
  eventType: TicketType;
  eventTrigger: TicketTrigger;
  actions: TicketActionWithSeverity[];
  potentiallyProtectableUsers: string[];
  startTime: number;
  endTime: number;
  creationTime: number;
  sections: TicketSection;
  noProtectedUsersAtCreation: boolean;
  socModuleEnabled: boolean;
  sensitiveDataRestricted: boolean;
  moduleName: SubscriptionModule;
  processedTime?: number;
  workspaceName?: string;
  processName?: string;
  socStatus?: SocStatus;
  processStatus?: EdrProcessStatus;
  lastLoggedInUsers?: string[];
  serviceName?: string;
  relatedTickets?: { eventId: string; eventTrigger: TicketTrigger }[];
}

interface TicketsStoreState {
  allTickets: number;
  pages: number;
  pagination: Pagination;
  tickets: Ticket[];
  sorting: {
    sortBy: string;
    sortDesc: boolean;
  };
  showSkeletonLoader: boolean;
  loading: boolean;
  search: string | null;
  ticketDetailsLoading: boolean;
  activityLogsLoading: boolean;
  isSoc: boolean;
  ticketDetails: TicketDetails;
  cursor: OpenSearchCursor;
}

export interface TicketSection {
  triggeredUser: string;
  serviceName: string;
  locations: Location[];
  counter: number;
  findings: Finding[];
  additionalFindings: Finding[];
  filesMetaData: FileMetaData[];
  securityAwarenessMetaData?: SecurityAwarenessMetaData;
  emailMetaData: EmailMetaData;
  malwareMetaData: MalwareMetaData;
  infectedProccessMetaData: InfectedProcessMetaData;
  anomalyDetectionMetaData: AnomalyDetectionMetaData;
  cloudThreatMetaData: ImpossibleTravelerDetails[];
  deviceMetaData: DeviceMetaData;
  networkMetaData: NetworkMetaData;
  cloudActivityLogsMetaData: { cloudActivityLogId: string; logType: string }[];
  userDataAccessViolation: UserDataAccessViolation;
  edrMetaData: EdrMetaData;
  relatedEvents?: { eventId: string; eventTrigger: TicketTrigger }[];
  systemMetaData: SystemMetaData;
  aggregatedEventTriggers?: TicketTrigger[];
  subTypes?: TicketEventSubtype[];
  maliciousUrlClickMetaData?: MaliciousUrlClickMetaData;
  recipients?: string[];
}

export interface TicketMetadata {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- metadata shape varies per ticket type; typed unions would require a runtime discriminator we don't have
  [key: string]: any;
}

export interface ApplyTicketBulkActionPayload {
  action: TicketAction;
  selection: ISelection<Ticket>;
  item?: Ticket;
  closeTicket?: boolean; // Used in soc
  metadata?: TicketMetadata;
}

const defaultTicketsState = {
  allTickets: 0,
  pages: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  tickets: [],
  sorting: {
    sortBy: "lastSeen",
    sortDesc: true,
  },
  showSkeletonLoader: true,
  loading: true,
  search: null,
  ticketDetailsLoading: false,
  activityLogsLoading: false,
  isSoc: false,
  ticketDetails: {
    activityLogs: [],
    activityLogsTotal: 0,
    processed: false,
    eventId: "",
    actions: [],
    potentiallyProtectableUsers: [],
    startTime: 0,
    endTime: 0,
    creationTime: 0,
    sections: {} as TicketSection,
    noProtectedUsersAtCreation: false,
    socModuleEnabled: false,
    sensitiveDataRestricted: false,
    lastLoggedInUsers: [],
    eventType: "" as TicketType,
    eventTrigger: "" as TicketTrigger,
    moduleName: "" as SubscriptionModule,
    relatedTickets: [],
  } as TicketDetails,
  cursor: {
    nextPage: firstPageQueryEntityActivityLogs,
  },
};

// Module-level abort controller for in-flight tickets list request cancellation.
// This is intentionally outside the store so it survives across store resets.
let ticketsAbortController: AbortController;

export const useTicketsStore = defineStore(StoreModule.TICKETS, {
  state: (): TicketsStoreState => ({ ...defaultTicketsState }),
  actions: {
    resetState() {
      this.$reset();
    },
    setPagination(pagination: Pagination) {
      this.pagination = pagination;
    },
    resetPagination() {
      this.pagination = {
        page: 0,
        pageSize: 25,
      };
    },
    resetActivityLogs() {
      this.resetCursor();
      this.ticketDetails.activityLogs = [];
      this.ticketDetails.activityLogsTotal = 0;
    },
    async getTickets(showSkeletonLoader = true) {
      ticketsAbortController = createAbortController(ticketsAbortController);
      const { signal } = ticketsAbortController;

      this.showSkeletonLoader = showSkeletonLoader;
      this.loading = true;

      const request = { ...getTicketsRequest(this.isSoc, this.pagination), signal };

      try {
        const tickets = await axiosInstance.request(request);

        this.tickets = tickets.data.items;
        this.allTickets = tickets.data.total;
      } catch (error) {
        if (error && (error as AxiosError).code === AxiosError.ERR_CANCELED) return;
        console.error(error);
      } finally {
        if (!signal.aborted) {
          this.showSkeletonLoader = false;
          this.loading = false;
        }
      }
    },
    applyPaginationChange(payload: Pagination) {
      this.setPagination(payload);
      this.getTickets();
    },
    async getTicketDetails({ eventId, workspaceId }: { eventId: string; workspaceId?: string }) {
      this.ticketDetailsLoading = true;
      useTicketStore().$patch({ workspaceId, eventId });

      const eventInfoRequest = getTicketDetailsRequest(this.isSoc, eventId);

      try {
        const { data } = await axiosInstance.request(eventInfoRequest);

        const ticketDetails = {
          ...data,
        };

        if (
          ticketDetails.eventType === TicketType.EMAIL_PHISHING &&
          ticketDetails.sections?.serviceName === Service.PROXY
        ) {
          const { direction, proxyRecipientsDomain } = {
            proxyRecipientsDomain: ticketDetails.sections?.emailMetaData?.proxyRecipientsDomain,
            direction: ticketDetails.sections?.emailMetaData?.emailProxyDirection!,
          };

          await useGatewaySettingsStore().getEmailProxyStatus(
            {
              direction,
            },
            proxyRecipientsDomain
          );
        }

        this.ticketDetails = {
          ...ticketDetails,
          // Activity logs are fetched via a separate endpoint and must not be overwritten here
          activityLogs: this.ticketDetails.activityLogs,
          activityLogsTotal: this.ticketDetails.activityLogsTotal,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.ticketDetailsLoading = false;
      }
    },
    async exportTicketsToCsv() {
      const filtersStore = useFiltersStore();

      const ticketFilters = { ...filtersStore.filters.ticketsFilters };
      const { widget, search, fromTime, toTime, protectedUsersOnly, processed } =
        convertTicketFiltersForBackend({
          ...filtersStore.filters.ticketsFilters,
        });

      const selectorModule = useSelectorStore();
      const request = {
        ...api.exportTicketsToCsv(),
        data: {
          filters: {
            widget,
            search,
            eventTriggers: ticketFilters.eventTriggers,
            fromTime,
            toTime,
            protectedUsersOnly,
            processed,
          },
          selection: getSelectionAsStringsArray<Ticket>(selectorModule.selection, "eventId"),
        },
      };
      try {
        await axiosInstance.request(request);

        const snackbarStore = useSnackbarStore();

        snackbarStore.addGenericSuccess(
          i18n.global.t("snackbar.messages.general.exportCsvStarted", {
            pageName: i18n.global.t("activityLogs.title"),
          })
        );
      } catch (err) {
        console.error(err);
      }
    },
    async executeTicketAction(
      payload: DialogDataConfig<
        TicketDetails & { name?: string; closeTicket?: boolean },
        TicketAction
      >
    ): Promise<void> {
      const request = buildTicketActionRequest(payload, this.isSoc);
      handleTicketMetadata(request, payload);

      return await axiosInstance.request(request);
    },
    async applyQuickFixAction(payload: DialogDataConfig<QuickFixPayload>) {
      await applyQuickFixAction(payload, this.getQuickFixCallback);
      await this.getTickets();
    },
    getQuickFixCallback(action: TicketAction, payload: DialogDataConfig<QuickFixPayload>) {
      switch (action) {
        case TicketAction.CONTACT_USER:
          const contactUserPayload: DialogDataConfig<{
            includeEventDetails: boolean;
            contactMessage: string;
            recipients: string[];
            id: string;
          }> = {
            ...payload,
            item: {
              ...payload.item.metadata[TicketAction.CONTACT_USER]!,
              id: payload.item.ticket.eventId,
            },
            selection: [],
          };
          return this.onContactUser(contactUserPayload);
        default:
          const actionToExecute =
            action === TicketAction.DISCARD_EMAIL_WITH_OPTIONS
              ? payload.item.metadata[TicketAction.DISCARD_EMAIL_WITH_OPTIONS]?.action!
              : action;

          const executeTicketActionPayload = {
            ...payload,
            item: {
              ...payload.item.ticket,
            },
            action: actionToExecute,
            metadata: {
              ...payload.item.metadata,
              action,
            },
            selection: [],
          };
          return this.executeTicketAction(executeTicketActionPayload);
      }
    },
    async applyTicketAction(
      payload: DialogDataConfig<
        TicketDetails & { name?: string; closeTicket?: boolean },
        TicketAction
      >
    ) {
      try {
        await this.executeTicketAction(payload);
        await this.getTickets();
        handleTicketActionSnackbar(payload, this.isSoc);
      } catch (err) {
        console.error(err);
      }
    },
    async applyTicketBulkAction(payload: ApplyTicketBulkActionPayload) {
      const { selection } = useSelectorStore();
      const requestData: ApplyTicketBulkActionPayload = {
        ...payload,
        selection,
      };

      try {
        const request = getTicketBulkActionRequest(this.isSoc, requestData);

        if (payload.metadata) {
          addGenericMetadataToRequest(request, payload.metadata);
        }

        const response = await axiosInstance.request(request);

        await this.getTickets();

        handleBulkActionSnackbar(requestData, response.data.appliedActions, this.isSoc);

        useSelectorStore().deselectAllPages();
      } catch (e) {
        console.error(e);
      }
    },
    async applyDownloadEmailAction(
      payload: DialogDataConfig<
        TicketDetails & { name?: string; closeTicket?: boolean },
        TicketAction
      >
    ) {
      const request = buildTicketActionRequest(payload, this.isSoc);

      try {
        const { data, headers } = await axiosInstance.request(request);
        const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
        const fileName = headers["x-coro-attachment-filename"];
        downloadFile(blob, fileName);

        await this.getTickets();

        const snackbarStore = useSnackbarStore();

        snackbarStore.addGenericSuccess(
          i18n.global.t(`snackbar.messages.event.${payload.action}`, {
            fileName,
          })
        );
      } catch (err) {
        console.error(err);
      }
    },
    async getTicketActivityLogs({
      eventId,
      workspaceId,
    }: {
      eventId: string;
      workspaceId?: string;
    }) {
      this.activityLogsLoading = true;

      try {
        const request = addWorkspaceHeaderIfNeeded(
          {
            ...api.getTicketActivityLogs(eventId),
            params: parseOpenSearchQuery(this.cursor.nextPage!),
          },
          workspaceId
        );

        const { data } = await axiosInstance.request(request);

        this.cursor = data.cursor;

        this.ticketDetails = {
          ...this.ticketDetails,
          activityLogs: handleVirtualScrollData(this.ticketDetails.activityLogs, data.items, "id"),
          activityLogsTotal: data.total,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.activityLogsLoading = false;
      }
    },
    async undoActivityLog(id: string) {
      this.activityLogsLoading = true;

      try {
        const request = addWorkspaceHeaderIfNeeded(
          { ...api.activityLogsUndo(id) },
          this.ticketDetails.workspaceId
        );

        await axiosInstance.request(request);
        this.resetCursor();
        await this.updateEventActivityLogs();
        await this.getTickets();
      } catch (error) {
        console.error(error);
      }

      this.activityLogsLoading = false;
    },
    async updateEventActivityLogs() {
      this.activityLogsLoading = true;
      try {
        const request = addWorkspaceHeaderIfNeeded(
          {
            ...api.getTicketActivityLogs(this.ticketDetails.eventId),
            params: parseOpenSearchQuery(this.cursor.nextPage!),
          },
          this.ticketDetails.workspaceId
        );
        const { data } = await axiosInstance.request(request);
        this.cursor = data.cursor;
        this.ticketDetails.activityLogs = data.items;
      } catch (e) {
        console.error(e);
      }

      this.activityLogsLoading = false;
    },
    async updateSocStatus(socStatus: SocStatus) {
      try {
        const socStatusRequest = {
          ...api.updateSocStatus(this.ticketDetails.eventId),
          data: {
            socStatus,
          },
        };
        await axiosInstance.request(socStatusRequest);
        this.getTickets();
      } catch (e) {
        console.error(e);
      }
    },
    async searchSocWorkspaces({
      search,
      page,
      pageSize,
    }: Pagination & { search: string }): Promise<{ workspaceId: string; workspaceName: string }[]> {
      try {
        const searchSocWorkspacesRequest = {
          ...api.searchSocWorkspaces({ search, page, pageSize }),
        };
        const { data } = await axiosInstance.request(searchSocWorkspacesRequest);
        return data;
      } catch (e) {
        console.error(e);
        return [];
      }
    },
    async onContactUser(
      payload: DialogDataConfig<{
        includeEventDetails: boolean;
        contactMessage: string;
        recipients: string[];
        id: string;
      }>
    ) {
      const workspaceStore = useWorkspaceStore();
      const workspaceDisplayName = workspaceStore.workspace.name;
      const request = {
        ...api.contactUser,
        params: {
          id: payload.item.id,
        },
        data: {
          contactMessage: payload.item.contactMessage,
          includeEventDetails: payload.item.includeEventDetails,
          recipients: payload.item.recipients,
          workspaceDisplayName,
          isSocView: this.isSoc,
        },
      };

      try {
        await axiosInstance.request(request);

        useSnackbarStore().addGenericSuccess(i18n.global.t("snackbar.messages.users.contactUser"));

        this.resetCursor();
        await this.updateEventActivityLogs();
      } catch (error) {
        console.error(error);
      }
    },
    getBulkActions(): Promise<AxiosResponse<{ items: TicketActionWithSeverity[] }>> {
      const filtersStore = useFiltersStore();

      const ticketFilters = { ...filtersStore.filters.ticketsFilters };
      const { widget, search, fromTime, toTime, protectedUsersOnly, processed } =
        convertTicketFiltersForBackend({
          ...filtersStore.filters.ticketsFilters,
        });

      const selectorModule = useSelectorStore();
      const request = {
        ...api.getAvailableTicketBulkActions(),
        data: {
          filter: {
            widget,
            search,
            eventTriggers: ticketFilters.eventTriggers,
            fromTime,
            toTime,
            protectedUsersOnly,
            processed,
          },
          selection: getSelectionAsStringsArray<Ticket>(selectorModule.selection, "eventId"),
        },
      };

      return axiosInstance.request(request);
    },
    resetCursor() {
      this.cursor = {
        nextPage: firstPageQueryEntityActivityLogs,
      };
    },
  },
});
